// src/portal-app/shell/kit/ConfirmSheet.js
//
// Glass bottom sheet for destructive confirmations. Cross-cutting by
// definition (spec §6) — moved here from operations/kit.js (Plan 4 Task 1).
// Class names (.opk-*) kept byte-identical; rules moved into kitCss.js.
//
// props:
//   open          {bool}    controls visibility
//   onClose       {fn}      backdrop tap / Escape
//   onConfirm     {fn}      confirm button
//   title         {string}
//   message       {string}
//   confirmLabel  {string}  defaults to t('confirm', {}, 'Confirm')
//   destructive   {bool}    confirm button uses --c-red
//   loading       {bool}    spinner + disabled
//   requiredPhrase{string}  user must type this to enable confirm
//   phraseHint    {string}  hint shown above the phrase input
//   reasonLabel   {string}  when set renders a text reason input
//   reasonValue   {string}  controlled value
//   onReasonChange{fn}      (value) => void
//   children      {node}    optional extra content (e.g. an inline
//                            error) rendered inside the sheet, above
//                            the action row — anything rendered as a
//                            sibling of ConfirmSheet instead sits
//                            behind its fixed, z-indexed backdrop and
//                            is invisible in the live app.
import React, { useState, useEffect, useId } from 'react';
import { createPortal } from 'react-dom';
import { GlassCard } from '../LiquidGlass';
import { useLanguage } from '../../../context/LanguageContext';
import { ensureKitCss } from './kitCss';

export function ConfirmSheet({
    open,
    onClose,
    onConfirm,
    title,
    message,
    confirmLabel,
    destructive = false,
    loading = false,
    requiredPhrase,
    phraseHint,
    reasonLabel,
    reasonValue = '',
    onReasonChange,
    children,
}) {
    ensureKitCss();
    const { t } = useLanguage();
    const [phrase, setPhrase] = useState('');
    const titleId = useId();

    // Reset phrase when sheet opens/closes
    useEffect(() => {
        if (!open) setPhrase('');
    }, [open]);

    // Escape key dismissal
    useEffect(() => {
        if (!open) return undefined;
        const handler = (e) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, [open, onClose]);

    if (!open) return null;

    const phraseMatch = !requiredPhrase || phrase === requiredPhrase;
    const confirmDisabled = loading || !phraseMatch;

    const btnClass = [
        'opk-confirm-btn',
        destructive ? 'opk-confirm-danger' : '',
    ].filter(Boolean).join(' ');

    // Portaled to document.body — NOT rendered inline. Per the CSS spec any
    // ancestor with a non-none `backdrop-filter` (or `filter`/`transform`/
    // `will-change`) becomes the CONTAINING BLOCK for `position:fixed`
    // descendants. This shell is built from glass panels (LiquidGlass sets
    // backdrop-filter), so `.opk-backdrop`'s `position:fixed` was never
    // relative to the viewport — it was relative to whichever glass panel
    // happened to enclose it, and `z-index:1000` only competed INSIDE that
    // panel's stacking context. The tab bar (`.psh-tabbar`, z-index:50) sits in
    // an outer context and therefore painted OVER the sheet, leaving the
    // Cancel/Confirm buttons unreachable — exactly what a real device showed
    // on the Account screen's Sign Out.
    //
    // CartPanel hit the identical trap during F12a QC and fixed it the same
    // way (see its header note): document.body is the one node in the tree
    // with no filter/backdrop-filter/transform ancestor, so portaling restores
    // true viewport-fixed positioning. The CSS is global regardless of where
    // the node mounts, so no rule changes were needed.
    return createPortal(
        <div
            className="opk-backdrop opk-backdrop-enter"
            data-testid="confirm-backdrop"
            onClick={(e) => {
                if (e.target === e.currentTarget) onClose();
            }}
            role="presentation"
        >
            <GlassCard
                veil
                className="opk-sheet opk-sheet-enter"
                data-testid="confirm-sheet"
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
            >
                <div id={titleId} className="opk-sheet-title">{title}</div>
                <div className="opk-sheet-msg">{message}</div>

                {reasonLabel && (
                    <>
                        <div className="opk-sheet-hint">{reasonLabel}</div>
                        <input
                            type="text"
                            className="opk-sheet-input"
                            data-testid="confirm-reason-input"
                            value={reasonValue}
                            placeholder={t('void_reason', {}, 'Enter reason')}
                            onInput={(e) => onReasonChange && onReasonChange(e.target.value)}
                            onChange={(e) => onReasonChange && onReasonChange(e.target.value)}
                        />
                    </>
                )}

                {requiredPhrase && (
                    <>
                        <div className="opk-sheet-hint">
                            {phraseHint || t('type_to_confirm', {}, 'Type to confirm')}
                        </div>
                        <input
                            type="text"
                            className="opk-sheet-input"
                            data-testid="confirm-phrase-input"
                            value={phrase}
                            placeholder={requiredPhrase}
                            onInput={(e) => setPhrase(e.target.value)}
                            onChange={(e) => setPhrase(e.target.value)}
                            autoComplete="off"
                        />
                    </>
                )}

                {children}

                <div className="opk-sheet-actions">
                    <button
                        type="button"
                        className="opk-sheet-cancel"
                        onClick={onClose}
                    >
                        {t('cancel', {}, 'Cancel')}
                    </button>
                    <button
                        type="button"
                        className={btnClass}
                        data-testid="confirm-btn"
                        disabled={confirmDisabled}
                        onClick={!confirmDisabled ? onConfirm : undefined}
                    >
                        {loading && (
                            <span
                                className="opk-spinner"
                                data-testid="confirm-spinner"
                                aria-hidden="true"
                            />
                        )}
                        {confirmLabel || t('confirm', {}, 'Confirm')}
                    </button>
                </div>
            </GlassCard>
        </div>,
        document.body,
    );
}

export default ConfirmSheet;
